<template>
  <transition name="nps">
    <aside v-if="open" class="nps" role="dialog" aria-label="New post" data-testid="nps">
      <div class="nps-head">
        <div>
          <span class="nps-kicker">New post</span>
          <div class="nps-channel">{{ channel && channel.name }} · {{ platformLabel }}</div>
        </div>
        <button class="nps-close" aria-label="Close" @click="$emit('close')">✕</button>
      </div>

      <!-- Setup: pick a capability to start -->
      <div v-if="content === null" class="nps-setup">
        <label class="nps-field">
          <span class="nps-field-label">What should this post be about?</span>
          <select v-model="capabilityId" class="nps-input" data-testid="nps-capability">
            <option value="">Choose a capability…</option>
            <option v-for="cap in capabilities" :key="cap.id" :value="cap.id">{{ cap.capability_name }}{{ cap.module_name ? ` · ${cap.module_name}` : '' }}</option>
          </select>
        </label>
        <label class="nps-field">
          <span class="nps-field-label">Direction (optional)</span>
          <input v-model.trim="extra" class="nps-input" placeholder="tone, angle, offer…" @keyup.enter="startDraft" />
        </label>
        <button class="nps-btn nps-btn-primary" :disabled="busy || (!capabilityId && !extra)" data-testid="nps-start" @click="startDraft">
          {{ busy ? 'Drafting… (~1 min)' : 'Draft with AI' }}
        </button>
        <p v-if="capabilities.length === 0" class="nps-hint">No capabilities synced yet — sync from Feature1, or just type a direction above.</p>
      </div>

      <!-- Working area: preview + chat + controls -->
      <template v-else>
        <!-- Live preview -->
        <div class="nps-preview">
          <div class="nps-preview-label">Preview</div>
          <p class="nps-preview-content">{{ content || '…' }}</p>
          <div v-if="image || generatingImage" class="nps-preview-media">
            <div v-if="generatingImage" class="nps-img-pending">Generating image…</div>
            <img v-else :src="image" class="nps-preview-img" alt="" />
          </div>
        </div>

        <!-- Chat thread (grows, scrollable) -->
        <div class="nps-thread" ref="threadEl">
          <div v-for="(m, i) in thread" :key="i" class="nps-msg" :class="`nps-msg-${m.role}`">
            <span class="nps-msg-role">{{ m.role === 'user' ? 'You' : 'AI' }}</span>
            <p class="nps-msg-text">{{ m.text }}</p>
          </div>
          <div v-if="busy" class="nps-msg nps-msg-assistant"><span class="nps-msg-role">AI</span><p class="nps-msg-text nps-typing">updating…</p></div>
        </div>

        <!-- Pinned bottom: media + schedule + post, then the chat input -->
        <div class="nps-bottom">
          <p v-if="error" class="nps-alert">{{ error }}</p>

          <div class="nps-media-controls">
            <button class="nps-btn nps-btn-ghost nps-btn-sm" :disabled="generatingImage" @click="genImage">{{ image ? 'Regenerate image' : 'Generate image' }}</button>
            <label class="nps-btn nps-btn-ghost nps-btn-sm">Upload<input type="file" accept="image/*,video/*" class="nps-file" @change="onUpload" /></label>
            <span v-for="(m, i) in media" :key="i" class="nps-chip">{{ m.name }}<button class="nps-chip-x" @click="media.splice(i, 1)">×</button></span>
          </div>

          <div class="nps-foot">
            <div class="nps-when">
              <label class="nps-radio"><input type="radio" value="now" v-model="type" /> Now</label>
              <label class="nps-radio"><input type="radio" value="schedule" v-model="type" /> Schedule</label>
              <input v-if="type === 'schedule'" v-model="date" type="datetime-local" class="nps-input nps-input-when" />
            </div>
            <button class="nps-btn nps-btn-primary" :disabled="busy || publishing || !content" data-testid="nps-post" @click="post">
              {{ publishing ? 'Posting…' : (type === 'schedule' ? 'Schedule' : 'Post now') }}
            </button>
          </div>

          <!-- Chat input at the very bottom -->
          <div class="nps-chatbar">
            <input v-model.trim="chatInput" class="nps-input" :disabled="busy" placeholder="Tell the AI how to change it — “make it shorter”, “add a stat”…" data-testid="nps-chat" @keyup.enter="sendChat" />
            <button class="nps-btn nps-btn-primary nps-btn-sm" :disabled="busy || !chatInput" @click="sendChat">Send</button>
          </div>
        </div>
      </template>
    </aside>
  </transition>
</template>

<script setup>
import { ref, reactive, computed, nextTick, watch } from 'vue'
import api from '../utils/api'
import { useSocialStore } from '../stores/social'

const props = defineProps({
  open: { type: Boolean, default: false },
  siteId: { type: String, required: true },
  channel: { type: Object, default: null },
  capabilities: { type: Array, default: () => [] },
})
const emit = defineEmits(['close', 'posted'])
const store = useSocialStore()

const PLATFORM_LABELS = { 'linkedin-page': 'LinkedIn', linkedin: 'LinkedIn', x: 'X', twitter: 'X', instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', tiktok: 'TikTok', threads: 'Threads' }
const platformLabel = computed(() => PLATFORM_LABELS[props.channel?.platform] || props.channel?.platform || '')
// The generator/image/publish pipeline keys posts by the internal id
// ('linkedin' not 'linkedin-page').
const internalPlatform = computed(() => store.toInternal(props.channel?.platform))

const capabilityId = ref('')
const extra = ref('')
const content = ref(null)
const postId = ref(null)
const image = ref(null)
const media = reactive([])
const thread = reactive([])
const chatInput = ref('')
const type = ref('now')
const date = ref('')
const busy = ref(false)
const generatingImage = ref(false)
const publishing = ref(false)
const error = ref('')
const threadEl = ref(null)

function reset() {
  capabilityId.value = ''; extra.value = ''; content.value = null; postId.value = null; image.value = null
  media.splice(0); thread.splice(0); chatInput.value = ''; type.value = 'now'; date.value = ''
  busy.value = false; generatingImage.value = false; publishing.value = false; error.value = ''
}
watch(() => props.open, (o) => { if (o) reset() })

const scrollThread = () => nextTick(() => { if (threadEl.value) threadEl.value.scrollTop = threadEl.value.scrollHeight })

async function startDraft() {
  busy.value = true; error.value = ''
  try {
    const cap = props.capabilities.find((c) => c.id === capabilityId.value) || { capability_name: extra.value || 'our product', description: '' }
    thread.push({ role: 'user', text: `Draft a post about ${cap.capability_name}${extra.value ? ` — ${extra.value}` : ''}.` })
    const data = await store.draftFromCapability(props.siteId, props.channel.platform, cap, extra.value)
    postId.value = data.postId || data.id || null
    if (postId.value) {
      const { data: detail } = await api.get(`/sites/${props.siteId}/social-posts/${postId.value}`)
      const post = detail.post || detail
      const variants = post.variants_by_platform || post.variantsByPlatform || {}
      const v = variants[internalPlatform.value] || variants[props.channel.platform]
      const text = Array.isArray(v) ? (v[0]?.text || v[0]) : (v?.text || v)
      content.value = (typeof text === 'string' ? text : '') || ''
    } else { content.value = '' }
    thread.push({ role: 'assistant', text: 'Here’s a first draft — tell me how to refine it, or add an image.' })
    scrollThread()
  } catch (e) { error.value = e?.response?.data?.error || e?.message || 'Drafting failed' } finally { busy.value = false }
}

async function sendChat() {
  const instruction = chatInput.value
  if (!instruction) return
  chatInput.value = ''
  thread.push({ role: 'user', text: instruction })
  scrollThread()
  busy.value = true; error.value = ''
  try {
    const revised = await store.refine(props.siteId, props.channel.platform, content.value, instruction)
    content.value = revised
    thread.push({ role: 'assistant', text: 'Updated the draft above.' })
    scrollThread()
  } catch (e) { error.value = e?.response?.data?.error || e?.message || 'Refine failed'; thread.push({ role: 'assistant', text: 'Sorry — could not apply that.' }) } finally { busy.value = false }
}

async function genImage() {
  generatingImage.value = true; error.value = ''
  try {
    const data = await store.generateImage(props.siteId, postId.value, internalPlatform.value)
    image.value = data.image?.url || data.url || data.imageUrl || null
    if (!image.value && data.image) image.value = data.image
  } catch (e) { error.value = e?.response?.data?.error || e?.message || 'Image generation failed' } finally { generatingImage.value = false }
}

function onUpload(e) {
  const files = Array.from(e.target.files || [])
  for (const file of files) {
    const reader = new FileReader()
    reader.onload = () => {
      media.push({ type: file.type.startsWith('video/') ? 'video' : 'image', dataUri: reader.result, name: file.name, size: file.size })
      if (!image.value && file.type.startsWith('image/')) image.value = reader.result
    }
    reader.readAsDataURL(file)
  }
  e.target.value = ''
}

async function post() {
  publishing.value = true; error.value = ''
  try {
    await store.publish(props.siteId, postId.value, {
      integrationId: props.channel.integration_id,
      platformId: internalPlatform.value,
      variantTier: 'short',
      type: type.value,
      date: date.value,
      mediaItems: media,
      content: content.value,
    })
    emit('posted')
    emit('close')
  } catch (e) { error.value = e?.response?.data?.error || e?.message || 'Posting failed' } finally { publishing.value = false }
}
</script>

<style scoped>
.nps { position: fixed; top: 0; right: 0; height: 100%; width: min(680px, 96vw); z-index: 300; display: flex; flex-direction: column;
  background: var(--surface-base, #12121c); border-left: var(--glass-border-hi); box-shadow: var(--elev-lg); color: var(--text-primary); }
.nps-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: var(--glass-border); }
.nps-kicker { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); }
.nps-channel { font-weight: 700; font-size: 0.95rem; margin-top: 2px; }
.nps-close { background: none; border: none; color: var(--text-secondary); font-size: 1rem; cursor: pointer; }

.nps-setup { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.nps-field { display: flex; flex-direction: column; gap: 5px; }
.nps-field-label { font-size: 0.78rem; font-weight: 600; color: var(--text-secondary); }
.nps-hint { font-size: 0.76rem; color: var(--text-muted); margin: 0; }

.nps-preview { margin: 14px 16px 0; padding: 12px 14px; border: var(--glass-border); border-radius: var(--radius-md); background: var(--glass-bg); }
.nps-preview-label { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); margin-bottom: 6px; }
.nps-preview-content { font-size: 0.9rem; line-height: 1.55; white-space: pre-wrap; margin: 0; color: var(--text-primary); }
.nps-preview-media { margin-top: 10px; }
.nps-preview-img { max-width: 100%; border-radius: var(--radius-sm); display: block; }
.nps-img-pending { font-size: 0.8rem; color: var(--text-secondary); }

.nps-thread { flex: 1; overflow-y: auto; padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; min-height: 60px; }
.nps-msg { max-width: 90%; }
.nps-msg-user { align-self: flex-end; text-align: right; }
.nps-msg-role { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); }
.nps-msg-text { margin: 2px 0 0; font-size: 0.82rem; line-height: 1.4; padding: 7px 10px; border-radius: 10px; background: var(--glass-bg-active); color: var(--text-primary); display: inline-block; }
.nps-msg-user .nps-msg-text { background: var(--accent-soft); }
.nps-typing { color: var(--text-secondary); font-style: italic; }

.nps-bottom { flex: none; background: var(--surface-base, #12121c); border-top: var(--glass-border); }
.nps-chatbar { display: flex; gap: 8px; padding: 10px 16px 14px; }
.nps-media-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 8px 16px; border-top: var(--glass-border); }
.nps-file { display: none; }
.nps-chip { font-size: 0.72rem; background: var(--glass-bg-active); border-radius: 999px; padding: 3px 9px; display: inline-flex; align-items: center; gap: 5px; }
.nps-chip-x { background: none; border: none; color: var(--text-secondary); cursor: pointer; }

.nps-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 16px; border-top: var(--glass-border); flex-wrap: wrap; }
.nps-when { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.nps-radio { font-size: 0.82rem; display: flex; align-items: center; gap: 5px; }
.nps-input { width: 100%; border: var(--glass-border); border-radius: var(--radius-sm); padding: 8px 10px; font-size: 0.85rem; background: var(--glass-bg-active); color: var(--text-primary); font-family: inherit; outline: none; color-scheme: dark; }
.nps-input:focus { border: var(--glass-border-hi); box-shadow: 0 0 0 3px var(--accent-soft); }
.nps-input-when { max-width: 210px; }
.nps-alert { color: var(--danger); font-size: 0.8rem; margin: 0 16px 12px; }

.nps-btn { border-radius: var(--radius-sm); padding: 8px 14px; font-size: 0.82rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.15s; display: inline-flex; align-items: center; gap: 5px; }
.nps-btn-sm { padding: 6px 10px; font-size: 0.78rem; }
.nps-btn-primary { background: var(--accent); color: #fff; box-shadow: 0 0 18px var(--accent-glow); }
.nps-btn-primary:hover { background: var(--accent-light); }
.nps-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
.nps-btn-ghost { background: var(--glass-bg); border: var(--glass-border); color: var(--text-primary); }
.nps-btn-ghost:hover { background: var(--glass-bg-hover); }

.nps-enter-active, .nps-leave-active { transition: transform 0.22s cubic-bezier(0.16,1,0.3,1); }
.nps-enter-from, .nps-leave-to { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) { .nps-enter-active, .nps-leave-active { transition: none; } }
</style>
